import { Grid } from '@mantine/core';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import { Show } from '../../../../../../react-commons/components/control-flow/Show';
import type { FormNames } from '../../../../../../react-commons/utils/form/getFormNames';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import { VCycleElementType } from '../../../../resource/enumerations/VCycleElementType';
import { VCycleSide } from '../../../../resource/enumerations/VCycleSide';
import {
    VCycleElementContainerPropertyValidation,
    type VCycleElementContainerResource,
} from '../../../../resource/resources/artifacts/use_case/representation/vcycleElementContainerResource';
import { PropertyInputs } from '../../../reusable-components/base/property-bases/PropertyInputs';
import type { PropertyValidationInformation } from '../../../util/PropertyValidationInformation';
import { VCycleElementDisplay } from './VCycleElementDisplay';
import { VCycleElementTable } from './VCycleElementTable';

export const VCycleElementContainerContent = ({
    id,
    projectId,
    projectVersionId,
    form,
    formNames,
    readOnly,
}: {
    id?: string;
    projectId: string | undefined | null;
    projectVersionId: string | undefined | null;
    form: UseFormReturn<VCycleElementContainerResource>;
    formNames: FormNames<VCycleElementContainerResource>;
    readOnly: boolean;
}) => {
    const [typeWatcher] = useWatch({
        control: form.control,
        name: [formNames.type],
    });

    const getPropertyValidationInformation = (
        propertyIdentifier: VCycleElementContainerPropertyValidation
    ): PropertyValidationInformation => ({
        resourceId: id,
        resourceType: ResourceType.V_CYCLE_ELEMENT_CONTAINER,
        propertyIdentifier,
    });

    return (
        <Grid>
            <Grid.Col span={6}>
                <PropertyInputs.Enum
                    name={formNames.side}
                    control={form.control}
                    enumType={VCycleSide}
                    nullable={false}
                    readOnly={readOnly}
                    propertyValidationInformation={getPropertyValidationInformation(
                        VCycleElementContainerPropertyValidation.SIDE
                    )}
                />
            </Grid.Col>
            <Grid.Col span={6}>
                <PropertyInputs.Integer
                    name={formNames.index}
                    control={form.control}
                    nullable={false}
                    readOnly={readOnly}
                    propertyValidationInformation={getPropertyValidationInformation(
                        VCycleElementContainerPropertyValidation.INDEX
                    )}
                />
            </Grid.Col>
            <Grid.Col span={6}>
                <PropertyInputs.Enum
                    name={formNames.type}
                    control={form.control}
                    enumType={VCycleElementType}
                    nullable={false}
                    readOnly={Boolean(id)}
                    propertyValidationInformation={getPropertyValidationInformation(
                        VCycleElementContainerPropertyValidation.TYPE
                    )}
                />
            </Grid.Col>
            <Grid.Col span={6}>
                <PropertyInputs.Integer
                    name={formNames.rowSpan}
                    control={form.control}
                    nullable={false}
                    readOnly={readOnly}
                    propertyValidationInformation={getPropertyValidationInformation(
                        VCycleElementContainerPropertyValidation.ROW_SPAN
                    )}
                />
            </Grid.Col>
            <Show when={id}>
                <Grid.Col span={12}>
                    {typeWatcher === VCycleElementType.REPRESENTATION ?
                        <VCycleElementDisplay projectId={projectId} projectVersionId={projectVersionId} parentId={id} />
                    :   <VCycleElementTable projectId={projectId} projectVersionId={projectVersionId} parentId={id} />}
                </Grid.Col>
            </Show>
        </Grid>
    );
};
